import { FilterFn, Row } from '@tanstack/react-table';
import { TanstackTableFilterVariant } from './types';

/** Strict equality against a single selected value. */
export const equalsValueFilter: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown
) => {
  if (filterValue === undefined || filterValue === null || filterValue === '') return true;
  return row.getValue(columnId) === filterValue;
};

/** Row passes when its value is one of the selected values. */
export const multiSelectFilter: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown
) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  return filterValue.includes(row.getValue(columnId));
};

/** Boolean equality; `filterValue` is a boolean or '' (= no filter). */
export const booleanFilter: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown
) => {
  if (filterValue === undefined || filterValue === null || filterValue === '') return true;
  return Boolean(row.getValue(columnId)) === Boolean(filterValue);
};

/** Inclusive [from, to] date-range filter. Bounds may be left open. */
export const dateRangeFilter: FilterFn<unknown> = (
  row: Row<unknown>,
  columnId: string,
  filterValue: unknown
) => {
  const [from, to] = (filterValue ?? []) as [string | undefined, string | undefined];
  if (!from && !to) return true;
  const raw = row.getValue(columnId);
  const time = raw instanceof Date ? raw.getTime() : new Date(raw as string).getTime();
  if (Number.isNaN(time)) return false;
  if (from && time < new Date(from).getTime()) return false;
  if (to && time > new Date(to).getTime()) return false;
  return true;
};

/**
 * Default `filterFn` for each variant. A built-in string name is returned where
 * TanStack already ships an equivalent; otherwise a custom function above.
 */
export function defaultFilterFnFor(
  variant: TanstackTableFilterVariant | undefined
): FilterFn<unknown> | string | undefined {
  switch (variant) {
    case 'text':
      return 'includesString';
    case 'number':
      return 'weakEquals';
    case 'range':
      return 'inNumberRange';
    case 'select':
      return equalsValueFilter;
    case 'multiSelect':
      return multiSelectFilter;
    case 'boolean':
      return booleanFilter;
    case 'date':
      return dateRangeFilter;
    default:
      return undefined;
  }
}
